import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  type CreateTemplatePayload,
  type TemplateCategory,
  type WhatsAppTemplate,
  whatsappService,
} from "@/services/whatsapp.service/whatsapp.api";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Hash,
  Link2,
  Loader2,
  Pencil,
  Phone,
  Plus,
  PlusCircle,
  Reply,
  Star,
  TicketIcon,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { type Control, useFieldArray, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: WhatsAppTemplate["status"] }) {
  const variants: Record<string, string> = {
    APPROVED: "bg-green-100 text-green-700 border-green-200",
    PENDING: "bg-yellow-100 text-yellow-700 border-yellow-200",
    REJECTED: "bg-red-100 text-red-700 border-red-200",
  };
  const labels: Record<string, string> = {
    APPROVED: "Aprovado",
    PENDING: "Pendente",
    REJECTED: "Rejeitado",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        variants[status] || "bg-muted text-muted-foreground border-border"
      }`}
    >
      {labels[status] || status}
    </span>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function bodyTextOf(t: WhatsAppTemplate): string {
  return t.components?.find((c) => c.type === "BODY")?.text || "";
}

/** A Meta só aceita snake_case em nomes de template — convertemos o texto livre digitado. */
function toSnakeCase(text: string): string {
  return (text || "")
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// ─── Variáveis do template (sistema + personalizadas) ─────────────────────────

/** Preenchidas automaticamente pelo Baita Ticket ao notificar um chamado — sempre nesta ordem. */
const SYSTEM_VARIABLES = [
  { token: "{{1}}", label: "Nome do cliente" },
  { token: "{{2}}", label: "Mensagem do atendimento" },
  { token: "{{3}}", label: "Link do chamado" },
];

/** Sugestão de exemplo para preencher {{1}}, {{2}}, {{3}} — a Meta exige uma amostra realista. */
const DEFAULT_VARIABLE_EXAMPLE: Record<number, string> = {
  1: "João Silva",
  2: "Sua solicitação foi atualizada",
  3: "https://baitaticket.com/c/abc123",
};

function nextCustomToken(text: string): string {
  const used = Array.from(text.matchAll(/\{\{(\d+)\}\}/g)).map((m) => Number(m[1]));
  const next = used.length ? Math.max(...used) + 1 : 1;
  return `{{${next}}}`;
}

/** Todos os números de variável usados no texto, em ordem (ex.: "Olá {{1}}, {{2}}" → [1, 2]). */
function extractVariableNumbers(text: string): number[] {
  const nums = new Set(Array.from(text.matchAll(/\{\{(\d+)\}\}/g)).map((m) => Number(m[1])));
  return Array.from(nums).sort((a, b) => a - b);
}

/** Rótulo amigável de cada variável, quando é uma das do sistema. */
function variableLabel(n: number): string {
  return SYSTEM_VARIABLES[n - 1]?.label || `Variável extra`;
}

function bodyExamplesOf(t: WhatsAppTemplate): Record<string, string> {
  const comp = t.components?.find((c) => c.type === "BODY");
  const values = comp?.example?.body_text?.[0] || [];
  const numbers = extractVariableNumbers(comp?.text || "");
  const map: Record<string, string> = {};
  numbers.forEach((n, i) => {
    map[String(n)] = values[i] || "";
  });
  return map;
}

/**
 * Campo de "Corpo da mensagem" com ajuda visual para inserir variáveis.
 * Pensado para quem nunca viu {{1}}/{{2}}: o texto some ao clicar e é
 * substituído pela informação real quando o WhatsApp envia a mensagem.
 *
 * Também pede um EXEMPLO de preenchimento para cada variável — a Meta rejeita
 * (com uma denúncia genérica de "política") qualquer template que use {{n}} sem
 * um exemplo de amostra, então isso não é opcional.
 */
function TemplateBodyField({
  value,
  onChange,
  examples,
  onExamplesChange,
}: {
  value: string;
  onChange: (value: string) => void;
  examples: Record<string, string>;
  onExamplesChange: (examples: Record<string, string>) => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const variableNumbers = extractVariableNumbers(value);

  function insertToken(token: string) {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el?.focus();
      const pos = start + token.length;
      el?.setSelectionRange(pos, pos);
    });
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-dashed border-border bg-muted/40 p-3">
        <p className="mb-2 text-xs text-muted-foreground">
          <strong>Como funciona:</strong> clique em um botão abaixo para inserir algo como{" "}
          <code>{"{{1}}"}</code> no texto. O cliente nunca vê esse código — na hora de enviar, o
          Baita Ticket troca automaticamente pela informação real.
        </p>
        <p className="mb-1.5 text-xs font-medium">
          Variáveis do sistema (preenchidas sozinhas ao notificar um chamado):
        </p>
        <div className="flex flex-wrap gap-1.5">
          {SYSTEM_VARIABLES.map((v) => (
            <Button
              key={v.token}
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 gap-1 px-2 text-xs"
              disabled={value.includes(v.token)}
              onClick={() => insertToken(v.token)}
            >
              <Hash className="h-3 w-3" />
              {v.label}
            </Button>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-2">
          <p className="text-xs text-muted-foreground">
            Precisa de outra informação que o sistema não tem? Adicione uma variável extra — só
            que essa não é preenchida automaticamente, então só use se for enviar esse template
            manualmente.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 shrink-0 gap-1 px-2 text-xs"
            onClick={() => insertToken(nextCustomToken(value))}
          >
            <Plus className="h-3 w-3" />
            Variável extra
          </Button>
        </div>
      </div>
      <Textarea
        ref={textareaRef}
        placeholder="Olá, {{1}}! {{2}} Acompanhe em: {{3}}"
        className="min-h-[100px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {variableNumbers.length > 0 && (
        <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
          <p className="text-xs text-amber-700 dark:text-amber-400">
            <strong>Obrigatório:</strong> a Meta rejeita o template se as variáveis não tiverem um
            exemplo de preenchimento (é assim que ela confere que o conteúdo é seguro). Preencha
            como se fosse um caso real.
          </p>
          {variableNumbers.map((n) => (
            <div key={n} className="space-y-1">
              <Label className="text-xs">
                Exemplo para <code>{`{{${n}}}`}</code> ({variableLabel(n)})
              </Label>
              <Input
                value={examples[String(n)] || ""}
                onChange={(e) => onExamplesChange({ ...examples, [String(n)]: e.target.value })}
                placeholder={n <= 3 ? DEFAULT_VARIABLE_EXAMPLE[n] : "Ex: valor de exemplo"}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Botões (opcional) ─────────────────────────────────────────────────────────

const BUTTON_TYPES = ["QUICK_REPLY", "URL", "PHONE_NUMBER"] as const;
type ButtonType = (typeof BUTTON_TYPES)[number];

const BUTTON_TYPE_LABEL: Record<ButtonType, string> = {
  QUICK_REPLY: "Resposta rápida",
  URL: "Abrir link",
  PHONE_NUMBER: "Ligar para número",
};

const BUTTON_TYPE_ICON: Record<ButtonType, typeof Reply> = {
  QUICK_REPLY: Reply,
  URL: Link2,
  PHONE_NUMBER: Phone,
};

const buttonSchema = z.object({
  type: z.enum(BUTTON_TYPES),
  text: z.string().min(1, "Texto obrigatório").max(25, "Máx. 25 caracteres"),
  url: z.string().optional(),
  phoneNumber: z.string().optional(),
});

type ButtonFormValue = z.infer<typeof buttonSchema>;

/**
 * Limites reais da Meta para o componente BUTTONS: até 10 resposta-rápida, até 2
 * link (URL), até 1 telefone, e até 10 botões no total. Dá pra misturar os tipos —
 * a Meta só exige que fiquem agrupados (resposta rápida de um lado, ação do outro),
 * o que a gente já garante ao montar os components em buildComponents().
 */
function validateButtons(buttons: ButtonFormValue[], ctx: z.RefinementCtx) {
  const quickReplyCount = buttons.filter((b) => b.type === "QUICK_REPLY").length;
  const urlCount = buttons.filter((b) => b.type === "URL").length;
  const phoneCount = buttons.filter((b) => b.type === "PHONE_NUMBER").length;

  if (buttons.length > 10) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["buttons"], message: "Máximo de 10 botões no total." });
  }
  if (quickReplyCount > 10) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["buttons"],
      message: "Máximo de 10 botões de resposta rápida.",
    });
  }
  if (urlCount > 2) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["buttons"], message: "Máximo de 2 botões de link." });
  }
  if (phoneCount > 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["buttons"], message: "Máximo de 1 botão de telefone." });
  }
  buttons.forEach((b, i) => {
    if (b.type === "URL" && !b.url?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["buttons", i, "url"], message: "Informe a URL" });
    }
    if (b.type === "PHONE_NUMBER" && !b.phoneNumber?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["buttons", i, "phoneNumber"], message: "Informe o telefone" });
    }
  });
}

function buttonsOf(t: WhatsAppTemplate): ButtonFormValue[] {
  const comp = t.components?.find((c) => c.type === "BUTTONS") as any;
  return (comp?.buttons || []).map((b: any) => ({
    type: (BUTTON_TYPES as readonly string[]).includes(b.type) ? b.type : "QUICK_REPLY",
    text: b.text || "",
    url: b.url,
    phoneNumber: b.phone_number,
  }));
}

/** Monta os components no formato que a Meta espera (BODY + BUTTONS, se houver). */
function buildComponents(bodyText: string, buttons: ButtonFormValue[], examples: Record<string, string>) {
  const variableNumbers = extractVariableNumbers(bodyText);
  const body: any = { type: "BODY", text: bodyText };
  if (variableNumbers.length) {
    // A Meta exige um exemplo de preenchimento para cada variável, na mesma ordem.
    body.example = { body_text: [variableNumbers.map((n) => examples[String(n)] || "")] };
  }

  const components: any[] = [body];
  if (buttons.length) {
    // A Meta exige que os tipos fiquem agrupados: todas as resposta-rápida primeiro, depois ação.
    const quickReplies = buttons.filter((b) => b.type === "QUICK_REPLY");
    const ctas = buttons.filter((b) => b.type !== "QUICK_REPLY");
    components.push({
      type: "BUTTONS",
      buttons: [...quickReplies, ...ctas].map((b) => {
        if (b.type === "URL") return { type: "URL", text: b.text, url: b.url };
        if (b.type === "PHONE_NUMBER") return { type: "PHONE_NUMBER", text: b.text, phone_number: b.phoneNumber };
        return { type: "QUICK_REPLY", text: b.text };
      }),
    });
  }
  return components;
}

/**
 * Editor de botões do template. Pensado para leigos: explica os dois grupos
 * (resposta rápida vs. ação) e só libera o botão de adicionar quando faz sentido.
 */
function TemplateButtonsEditor({ control }: { control: Control<any> }) {
  const { fields, append, remove } = useFieldArray({ control, name: "buttons" });
  const buttons = (useWatch({ control, name: "buttons" }) as ButtonFormValue[] | undefined) || [];
  const quickReplyCount = buttons.filter((b) => b?.type === "QUICK_REPLY").length;
  const urlCount = buttons.filter((b) => b?.type === "URL").length;
  const phoneCount = buttons.filter((b) => b?.type === "PHONE_NUMBER").length;
  const canAddQuickReply = quickReplyCount < 10 && buttons.length < 10;
  const canAddUrl = urlCount < 2 && buttons.length < 10;
  const canAddPhone = phoneCount < 1 && buttons.length < 10;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium">Botões (opcional)</p>
        <p className="text-xs text-muted-foreground">
          Botões aparecem embaixo da mensagem no WhatsApp. Você pode combinar até{" "}
          <strong>10 respostas rápidas</strong>, <strong>2 links</strong> e{" "}
          <strong>1 telefone</strong> no mesmo template (máximo de 10 botões no total) — o Baita
          Ticket organiza a ordem automaticamente para a Meta aceitar.
        </p>
      </div>

      {fields.map((field, index) => {
        const kind: ButtonType = buttons[index]?.type || "QUICK_REPLY";
        return (
          <div
            key={field.id}
            className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-start"
          >
            <FormField
              control={control}
              name={`buttons.${index}.type`}
              render={({ field }) => (
                <FormItem className="w-full sm:w-40">
                  <FormLabel className="text-xs">Tipo</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {BUTTON_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {BUTTON_TYPE_LABEL[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`buttons.${index}.text`}
              render={({ field }) => (
                <FormItem className="w-full flex-1">
                  <FormLabel className="text-xs">Texto do botão (máx. 25 caracteres)</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Rastrear pedido" maxLength={25} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {kind === "URL" && (
              <FormField
                control={control}
                name={`buttons.${index}.url`}
                render={({ field }) => (
                  <FormItem className="w-full flex-1">
                    <FormLabel className="text-xs">Link (URL)</FormLabel>
                    <FormControl>
                      <Input placeholder="https://minhaempresa.com/pedido" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {kind === "PHONE_NUMBER" && (
              <FormField
                control={control}
                name={`buttons.${index}.phoneNumber`}
                render={({ field }) => (
                  <FormItem className="w-full flex-1">
                    <FormLabel className="text-xs">Telefone (com código do país)</FormLabel>
                    <FormControl>
                      <Input placeholder="5511999999999" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="mt-5 h-9 w-9 shrink-0 text-destructive hover:text-destructive"
              aria-label="Remover botão"
              onClick={() => remove(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      })}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={!canAddQuickReply}
          onClick={() => append({ type: "QUICK_REPLY", text: "" })}
        >
          <Plus className="h-3.5 w-3.5" />
          <Reply className="h-3.5 w-3.5" />
          Adicionar resposta rápida ({quickReplyCount}/10)
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={!canAddUrl}
          onClick={() => append({ type: "URL", text: "", url: "" })}
        >
          <Plus className="h-3.5 w-3.5" />
          <Link2 className="h-3.5 w-3.5" />
          Adicionar link ({urlCount}/2)
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={!canAddPhone}
          onClick={() => append({ type: "PHONE_NUMBER", text: "", phoneNumber: "" })}
        >
          <Plus className="h-3.5 w-3.5" />
          <Phone className="h-3.5 w-3.5" />
          Adicionar telefone ({phoneCount}/1)
        </Button>
      </div>
    </div>
  );
}

// ─── Create form schema ───────────────────────────────────────────────────────

/** A Meta rejeita templates com {{n}} sem um exemplo de preenchimento para cada variável. */
function validateBodyExamples(bodyText: string, examples: Record<string, string>, ctx: z.RefinementCtx) {
  const missing = extractVariableNumbers(bodyText).filter((n) => !examples[String(n)]?.trim());
  if (missing.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["bodyExamples"],
      message: `Preencha o exemplo de ${missing.map((n) => `{{${n}}}`).join(", ")} — a Meta exige isso.`,
    });
  }
}

const createSchema = z
  .object({
    name: z
      .string()
      .min(1, "Nome obrigatório")
      .refine((v) => !!toSnakeCase(v), "Use letras e números"),
    category: z.enum(["MARKETING", "UTILITY", "AUTHENTICATION"] as const),
    language: z.string().min(2, "Idioma obrigatório"),
    bodyText: z.string().min(1, "Texto do corpo obrigatório").max(1024),
    bodyExamples: z.record(z.string()).default({}),
    buttons: z.array(buttonSchema).max(10, "Máximo de 10 botões").default([]),
  })
  .superRefine((data, ctx) => {
    validateButtons(data.buttons, ctx);
    validateBodyExamples(data.bodyText, data.bodyExamples, ctx);
  });

type CreateForm = z.infer<typeof createSchema>;

// ─── Edit form schema (nome e idioma não podem ser alterados na Meta) ─────────

const editSchema = z
  .object({
    category: z.enum(["MARKETING", "UTILITY", "AUTHENTICATION"] as const),
    bodyText: z.string().min(1, "Texto do corpo obrigatório").max(1024),
    bodyExamples: z.record(z.string()).default({}),
    buttons: z.array(buttonSchema).max(10, "Máximo de 10 botões").default([]),
  })
  .superRefine((data, ctx) => {
    validateButtons(data.buttons, ctx);
    validateBodyExamples(data.bodyText, data.bodyExamples, ctx);
  });

type EditForm = z.infer<typeof editSchema>;

// ─── Painel de edição (inline, igual ao de criar — não é modal) ───────────────

function EditTemplateForm({
  template,
  onClose,
  onSaved,
}: {
  template: WhatsAppTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<EditForm>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      category: template.category,
      bodyText: bodyTextOf(template),
      bodyExamples: bodyExamplesOf(template),
      buttons: buttonsOf(template),
    },
  });

  async function onSubmit(values: EditForm) {
    setSubmitting(true);
    const [res, err] = await whatsappService.updateTemplate(template.id, {
      category: values.category,
      components: buildComponents(values.bodyText, values.buttons, values.bodyExamples),
    });
    setSubmitting(false);

    if (err || !res) {
      toast.error(err?.msg || "Erro ao editar template");
      return;
    }

    toast.success("Template atualizado e reenviado para aprovação!");
    onSaved();
  }

  return (
    <div className="rounded-lg border border-border p-4">
      <h4 className="mb-4 font-medium">Editar "{template.name}"</h4>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Categoria</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Categoria" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="MARKETING">Marketing</SelectItem>
                    <SelectItem value="UTILITY">Utilitário</SelectItem>
                    <SelectItem value="AUTHENTICATION">Autenticação</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="bodyText"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Corpo da mensagem</FormLabel>
                <FormControl>
                  <TemplateBodyField
                    value={field.value}
                    onChange={field.onChange}
                    examples={form.watch("bodyExamples")}
                    onExamplesChange={(examples) => form.setValue("bodyExamples", examples)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <TemplateButtonsEditor control={form.control} />
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting} className="gap-2">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar e reenviar
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Templates() {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ticketTemplateName, setTicketTemplateName] = useState<string | null>(null);
  const [settingTicket, setSettingTicket] = useState<string | null>(null);
  const [editing, setEditing] = useState<WhatsAppTemplate | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteFailure, setDeleteFailure] = useState<{ template: WhatsAppTemplate; message: string } | null>(null);

  const form = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: "",
      category: "UTILITY",
      language: "pt_BR",
      bodyText: "",
      bodyExamples: {},
      buttons: [],
    },
  });

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    const [data, err] = await whatsappService.listTemplates();
    setLoading(false);
    if (err) {
      toast.error("Erro ao buscar templates");
      return;
    }
    setTemplates(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  async function onSubmit(values: CreateForm) {
    setSubmitting(true);
    const payload: CreateTemplatePayload = {
      name: toSnakeCase(values.name),
      category: values.category as TemplateCategory,
      language: values.language,
      components: buildComponents(values.bodyText, values.buttons, values.bodyExamples),
    };

    const [res, err] = await whatsappService.createTemplate(payload);
    setSubmitting(false);

    if (err || !res) {
      toast.error(err?.msg || "Erro ao criar template");
      return;
    }

    toast.success("Template enviado para aprovação!");
    form.reset();
    setShowForm(false);
    void fetchTemplates();
  }

  async function handleSetTicketTemplate(t: WhatsAppTemplate) {
    setSettingTicket(t.name);
    const [res, err] = await whatsappService.setTicketTemplate(t.name, t.language);
    setSettingTicket(null);

    if (err || !res?.success) {
      toast.error("Erro ao definir template de chamado");
      return;
    }

    setTicketTemplateName(res.ticketTemplateName);
    toast.success(`"${res.ticketTemplateName}" definido como template de chamado`);
  }

  async function handleDelete(t: WhatsAppTemplate, localOnly = false) {
    setDeletingId(t.id);
    const [res, err] = await whatsappService.deleteTemplate(t.id, { localOnly });
    setDeletingId(null);

    if (err || !res?.success) {
      if (!localOnly) {
        setDeleteFailure({ template: t, message: err?.msg || "Falha ao remover template na Meta" });
      } else {
        toast.error(err?.msg || "Erro ao remover template");
      }
      return;
    }

    setDeleteFailure(null);
    toast.success(`"${t.name}" removido`);
    void fetchTemplates();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Templates de mensagem</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setEditing(null);
            setShowForm((v) => !v);
          }}
          className="gap-2"
        >
          <PlusCircle className="h-4 w-4" />
          Novo template
        </Button>
      </div>

      {/* Edit form (inline, no lugar do de criar) */}
      {editing && (
        <EditTemplateForm
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void fetchTemplates();
          }}
        />
      )}

      {/* Create form */}
      {!editing && showForm && (
        <div className="rounded-lg border border-border p-4">
          <h4 className="mb-4 font-medium">Criar template</h4>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome do template</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Atualização de pedido" {...field} />
                      </FormControl>
                      {field.value && (
                        <p className="text-xs text-muted-foreground">
                          Será salvo como: <code>{toSnakeCase(field.value) || "—"}</code>
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Categoria" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="MARKETING">Marketing</SelectItem>
                          <SelectItem value="UTILITY">Utilitário</SelectItem>
                          <SelectItem value="AUTHENTICATION">Autenticação</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="language"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Idioma</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Idioma" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="pt_BR">Português (BR)</SelectItem>
                          <SelectItem value="en_US">English (US)</SelectItem>
                          <SelectItem value="es">Español</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="bodyText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Corpo da mensagem</FormLabel>
                    <FormControl>
                      <TemplateBodyField
                        value={field.value}
                        onChange={field.onChange}
                        examples={form.watch("bodyExamples")}
                        onExamplesChange={(examples) => form.setValue("bodyExamples", examples)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <TemplateButtonsEditor control={form.control} />
              <div className="flex gap-2">
                <Button type="submit" disabled={submitting} className="gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Enviar para aprovação
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    form.reset();
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </Form>
        </div>
      )}

      {/* Templates list */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nenhum template encontrado. Crie o primeiro template acima.
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => {
            const isActiveTicketTemplate = ticketTemplateName === t.name;
            const isSettingThis = settingTicket === t.name;
            const isDeletingThis = deletingId === t.id;
            const templateButtons = buttonsOf(t);
            return (
              <div
                key={t.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium text-sm">{t.name}</p>
                    {isActiveTicketTemplate && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 border border-amber-200 shrink-0">
                        <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                        Template de chamado
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t.category} · {t.language}
                  </p>
                  {t.status === "REJECTED" && t.rejected_reason && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      Motivo: {t.rejected_reason}
                    </p>
                  )}
                  {templateButtons.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {templateButtons.map((b, i) => {
                        const Icon = BUTTON_TYPE_ICON[b.type];
                        return (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                          >
                            <Icon className="h-3 w-3" />
                            {b.text}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={t.status} />
                  {t.status === "APPROVED" && !isActiveTicketTemplate && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 text-xs h-7 px-2"
                      disabled={isSettingThis}
                      onClick={() => void handleSetTicketTemplate(t)}
                    >
                      {isSettingThis ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <TicketIcon className="h-3 w-3" />
                      )}
                      Usar para chamados
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    aria-label="Editar"
                    onClick={() => {
                      setShowForm(false);
                      setEditing(t);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        aria-label="Excluir"
                        disabled={isDeletingThis}
                      >
                        {isDeletingThis ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Excluir "{t.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Isso remove o template na Meta e no Baita Ticket. Essa ação não pode
                          ser desfeita.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => void handleDelete(t)}>
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!deleteFailure} onOpenChange={(open) => !open && setDeleteFailure(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Não deu para remover "{deleteFailure?.template.name}" na Meta</DialogTitle>
            <DialogDescription>
              A Meta recusou o pedido de exclusão. Isso normalmente é falta de permissão de
              gerenciamento no token conectado — verifique em Business Settings da Meta se o
              usuário/token tem acesso de "Controle total" nesta conta do WhatsApp.
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
            {deleteFailure?.message}
          </p>
          <p className="text-sm text-muted-foreground">
            Você pode remover apenas daqui do Baita Ticket — o template <strong>continua existindo
            no WhatsApp</strong> e precisa ser excluído manualmente lá (WhatsApp Manager).
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteFailure(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!!deletingId}
              className="gap-2"
              onClick={() => deleteFailure && void handleDelete(deleteFailure.template, true)}
            >
              {deletingId && <Loader2 className="h-4 w-4 animate-spin" />}
              Remover apenas do Baita Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
